import React, { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { toAbsoluteUrl } from '../../../../_ens/_helpers';
import useSTAI2Availability from '../hooks/useSTAI2Availability';
import { isProvinceAllowedForSTAI2 } from '../VideoCall/stai2Service';
import { getAllProjects } from '../../../services/projects.service';
import '../styles/ProjectSelection.scss';

const ProjectSelection = () => {
  console.log('ProjectSelection - Component rendering');
  const history = useHistory();
  const [projects, setProjects] = useState([]);
  const [projectsLoading, setProjectsLoading] = useState(true);
  const [userProvince, setUserProvince] = useState(null);
  const [availabilityCheckComplete, setAvailabilityCheckComplete] = useState(false);

  // Use STAI2 availability hook for dynamic province checking
  const stai2Availability = useSTAI2Availability(userProvince);
  const {
    isAvailable,
    loading: availabilityLoading,
    hasRestrictions,
    isAllProvincesAllowed,
    getAllowedProvinceCodes
  } = stai2Availability;

  useEffect(() => {
    console.log('ProjectSelection - useEffect called');
    loadProjects();
    checkGeolocationAccess();
  }, []);

  // Load projects from API
  const loadProjects = async () => {
    try {
      setProjectsLoading(true);
      console.log('📦 Loading projects from API...');
      const response = await getAllProjects();
      const projectsData = response.data || response;
      console.log('✅ Projects loaded:', projectsData);
      setProjects(projectsData);
    } catch (error) {
      console.error('❌ Error loading projects:', error);
      // Fallback to empty array if API fails
      setProjects([]);
    } finally {
      setProjectsLoading(false);
    }
  };

  // Update STAI2 visibility based on province availability
  useEffect(() => {
    if (availabilityCheckComplete && !availabilityLoading) {
      console.log('🌍 STAI2 - Availability check result:', {
        userProvince,
        isAvailable,
        isAllProvincesAllowed,
        hasRestrictions,
        allowedProvinces: getAllowedProvinceCodes ? getAllowedProvinceCodes() : null
      });
    }
  }, [isAvailable, availabilityCheckComplete, availabilityLoading, userProvince, getAllowedProvinceCodes]);

  // Debug helpers for console testing
  useEffect(() => {
    window.stai2Debug = {
      testProvince: async (province) => {
        console.log('🧪 Testing province:', province);
        const result = await isProvinceAllowedForSTAI2(province);
        console.log(`Result for ${province}:`, result);
        return result;
      },
      getCurrentState: () => ({
        userProvince,
        isAvailable,
        isAllProvincesAllowed,
        hasRestrictions,
        allowedProvinces: getAllowedProvinceCodes ? getAllowedProvinceCodes() : null,
        availabilityCheckComplete,
        availabilityLoading
      })
    };
    console.log('🛠️ Debug helpers available: window.stai2Debug');
  }, [userProvince, isAvailable, isAllProvincesAllowed, hasRestrictions, getAllowedProvinceCodes, availabilityCheckComplete, availabilityLoading]);

  const checkGeolocationAccess = async () => {
    console.log('🌍 STAI2 - Starting location-based availability check');

    // Always try geolocation - if there are no restrictions, project will be shown anyway
    // If there are restrictions but geolocation fails, project will be hidden for security
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        async (position) => {
          const { latitude, longitude } = position.coords;
          console.log('📍 STAI2 - Coordinates detected:', latitude, longitude);

          try {
            // Use reverse geocoding to get province from coordinates
            const province = await reverseGeocode(latitude, longitude);
            console.log('🏷️ STAI2 - Province determined:', province);

            setUserProvince(province);
            setAvailabilityCheckComplete(true);

          } catch (error) {
            console.warn('⚠️ STAI2 - Reverse geocoding failed:', error);
            // If geocoding fails, we don't know the province
            setUserProvince(null);
            setAvailabilityCheckComplete(true);
          }
        },
        (error) => {
          console.warn('⚠️ STAI2 - Geolocation failed:', error);
          // If geolocation fails, we can't determine province
          setUserProvince(null);
          setAvailabilityCheckComplete(true);
        },
        {
          enableHighAccuracy: true,
          timeout: 10000,
          maximumAge: 300000
        }
      );
    } else {
      console.log('⚠️ STAI2 - Geolocation not supported by browser');
      // Browser doesn't support geolocation
      setUserProvince(null);
      setAvailabilityCheckComplete(true);
    }
  };

  // Simple reverse geocoding function (can be replaced with more sophisticated service)
  const reverseGeocode = async (latitude, longitude) => {
    try {
      console.log('📍 STAI2 - Starting reverse geocoding for:', latitude, longitude);

      // Using OpenStreetMap Nominatim (free service)
      // In production, consider using Google Maps or other premium services
      const url = `https://nominatim.openstreetmap.org/reverse?format=json&lat=${latitude}&lon=${longitude}&zoom=10&addressdetails=1`;
      console.log('🌐 STAI2 - Geocoding URL:', url);

      const response = await fetch(url);

      if (!response.ok) throw new Error('Geocoding failed');

      const data = await response.json();
      console.log('📍 STAI2 - Geocoding raw response:', data);

      // Extract province code from the response using ISO3166-2-lvl6
      const address = data.address;
      console.log('🏠 STAI2 - Address details:', address);

      // Use ISO3166-2-lvl6 field (format: IT-CR, IT-MI, etc.)
      const provinceISO = address['ISO3166-2-lvl6'];
      console.log('🏷️ STAI2 - Detected province ISO code:', provinceISO);

      if (provinceISO && provinceISO.startsWith('IT-')) {
        const finalCode = provinceISO; // Keep full format IT-CR
        console.log('✅ STAI2 - Using ISO province code:', finalCode);
        return finalCode;
      }

      // Fallback: if ISO field not available, return null (will trigger access denial if restrictions exist)
      console.warn('⚠️ STAI2 - No valid ISO province code found');
      return null;

    } catch (error) {
      console.error('❌ Reverse geocoding error:', error);
      throw error;
    }
  };

  const selectProject = (project) => {
    console.log('🎯 Selected project:', project);

    if (project.code === "CSB") {
      history.push('/auth/login');
    } else {
      history.push(`/guest?id=${project.id}`);
    }
  };

  const projectIsGeoLimited = (locationsAllowed) => {
    return Array.isArray(locationsAllowed) && locationsAllowed.length > 0;
  }

  const isProjectUnavailable = (projectCode, locationsAllowed) => {
    if(!projectIsGeoLimited(locationsAllowed)) {
        return false;
    }
    console.log('Checking availability for project:', projectCode, 'with locations:', locationsAllowed, 'userProvince:', userProvince);

    if (projectCode !== 'CSB') {
        return locationsAllowed.filter(province => province.code === userProvince).length === 0;
    }
    return false;
  }
      // STAI2 specific availability

  const ProjectCard = ({ project, onClick, disabled, loading, unavailable, imageUrl }) => {
    const type = project?.code?.toLowerCase() || 'default';
    const title = project?.name || project?.title || 'Unknown Project';

    const boxClass = `project-box ${type}-box ${loading ? 'loading' : ''} ${unavailable ? 'unavailable' : ''} ${!loading && !unavailable ? 'available' : ''}`;

    return (
      <div className={boxClass} onClick={!disabled && !loading ? onClick : undefined}>
        <div className="project-icon">
          <img
            src={imageUrl}
            alt={title}
            style={unavailable ? { opacity: 0.4 } : undefined}
          />
        </div>
        <h5 className="main-title" style={unavailable ? { opacity: 0.5 } : undefined}>
          {title}
        </h5>
        {loading ? (
          <div className="loading-spinner">
            <div className="spinner-border" role="status">
              <span className="sr-only">Loading...</span>
            </div>
          </div>
        ) : unavailable ? (
          <>
            <p className="unavailable-text">Non disponibile</p>
          </>
        ) : (
          <button
            className="project-btn"
            style={project.color ? { backgroundColor: project.color } : {}}
          >
            ACCEDI
          </button>
        )}
      </div>
    );
  };

  return (
    <div className="project-selection-screen">
      <div className="container py-0">
        <div className="row mb-4">
          <div className="col-12 text-center">
            <h1 className="main-title">Seleziona il Servizio</h1>
          </div>
        </div>

        <div className="projects-container">
          {projectsLoading ? (
            <div className="text-center w-100">
              <div className="spinner-border" role="status">
                <span className="sr-only">Caricamento progetti...</span>
              </div>
            </div>
          ) : projects.length === 0 ? (
            <div className="alert alert-info text-center w-100">
              Nessun progetto disponibile al momento.
            </div>
          ) : (
            projects.map((project) => {
                if (project.is_active) {
                  return (
                    <ProjectCard
                      key={project.id}
                      project={project}
                      onClick={() => selectProject(project)}
                      disabled={projectIsGeoLimited(project.locations_allowed) && !userProvince}
                      unavailable={isProjectUnavailable(project.code, project.locations_allowed)}
                      imageUrl={project.logo}
                    />
                  );
                }
            })
          )}
        </div>

        {/* User feedback on service availability */}
        {availabilityCheckComplete && !userProvince && hasRestrictions && (
          <div className="row mt-3">
            <div className="col-12">
              <div className="alert alert-warning text-center">
                <small>
                  ⚠️ Impossibile determinare la posizione. Alcuni servizi richiedono geolocalizzazione per verificare la disponibilità nella tua area.
                </small>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ProjectSelection;
